import { useBemCN } from '@gik/core/utils/bemBlock';
import withComponentErrorBoundary from '@gik/core/utils/withComponentErrorBoundary';
import React from 'react';
import { Layout } from '@gik/ui/gik/Layout/Layout';
import { Button } from '@gik/ui/Button';
import { Image } from '@gik/ui/Image';
import { SvgIcon } from '@gik/ui/SvgIcon';
import ChevronRightIcon from '@heroicons/react/solid/ChevronRightIcon';
import type { LandingPageBlogPost } from '../types/LandingPageBlogPost';
import { Analytics } from '@gik/analytics';
import { AnalyticsEvents } from '@gik/analytics/utils/Events';
import { navigateTo } from '@/utils/navigateTo';

export interface IBlogCardProps {
  post: LandingPageBlogPost;
  style?: React.CSSProperties;
}

/**
 * https://www.figma.com/design/FT2ninjhNiQOXfnOTaZfqy/%F0%9F%91%A8%F0%9F%8F%BC%E2%80%8D%F0%9F%8E%A4-Jade's-workspace-of-asset-creation?node-id=4951-11113&m=dev
 */
function BlogCardComp({
  post: { icon, title, content, buttonTitle = 'Read more', buttonLink },
  style,
}: IBlogCardProps) {
  const bem = useBemCN('blog-card');

  const onBlogClicked = React.useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      Analytics.fireEvent(AnalyticsEvents.BlogClicked, {
        feature: title,
        location: window.location.pathname,
      });

      if (buttonLink) navigateTo(buttonLink);
    },
    [buttonLink, title]
  );

  return (
    <Layout style={style} {...bem()} direction="column" gap={6}>
      {icon && (
        <Layout
          element="aside"
          direction={'row'}
          align={'center'}
          perpendicular={'center'}
          style={{
            flexGrow: 0,
            flexShrink: 0,
          }}
        >
          <div>
            <Image src={icon} alt={'icon'} className={'tw-rounded'} />
          </div>
        </Layout>
      )}
      <Layout direction={'column'} align={'center'} perpendicular={'start'} gap={6}>
        <h2 className={titleClassNames.join(' ')}>{title}</h2>
        <p className={paragraphClassNames.join(' ')}>{content}</p>
        <Button
          size={'sm'}
          type={'a'}
          href={buttonLink}
          onClick={onBlogClicked}
          variant={'primary-link'}
          append={<SvgIcon Icon={ChevronRightIcon} />}
        >
          {buttonTitle}
        </Button>
      </Layout>
    </Layout>
  );
}

const titleClassNames = ['tw-m-0', 'tw-text-gray-900', 'tw-text-2xl', 'tw-font-bold', 'tw-leading-tight'];

const paragraphClassNames = [
  'tw-m-0',
  'tw-text-gray-500',
  'tw-font-roboto',
  'tw-text-base',
  'tw-font-normal',
  'tw-leading-5',
];

export const BlogCard = withComponentErrorBoundary(BlogCardComp);
